import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Bug, AlertCircle, Users, Trash2, Calendar, FolderGit2, ArrowRight, Activity } from 'lucide-react';
import { apiClient } from '../utils/api';
import { colors } from '../utils/constants';
import { Brain } from 'lucide-react'; // Added for indicators
import './DashboardPage.css';

function getSuggestedSeverity(bug) {
  const manual = (bug.severity || 'medium').toLowerCase();
  if (!bug.ai_analysis) return manual;

  const text = bug.ai_analysis.toUpperCase();
  const options = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
  const weights = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };

  let aiVerdict = null;
  for (const opt of options) {
    // Support variants like "SEVERITY: CRITICAL" or "CRITICAL SEVERITY"
    if (text.includes(`SEVERITY: ${opt}`) || text.includes(`SEVERITY ${opt}`) || text.includes(`${opt} SEVERITY`)) {
      aiVerdict = opt.toLowerCase();
      break;
    }
  }

  if (aiVerdict && weights[aiVerdict] > weights[manual]) {
    return aiVerdict;
  }
  return manual;
}



export default function DashboardPage({ company, onSelectTest, onViewChange, showToast }) {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  const loadTests = useCallback(async () => {
    try {
      setLoading(true);
      let enriched = [];
      try {
        const data = await apiClient.getTests(company.id);
        enriched = await Promise.all(data.map(async (test) => {
          try {
            const bugs = await apiClient.getBugs(test.id);
            const upgradedCriticals = bugs.filter(b => getSuggestedSeverity(b) === 'critical').length;
            return {
              ...test,
              bug_count: bugs.length,
              critical_count: upgradedCriticals
            };
          } catch (e) {
            return test;
          }
        }));
      } catch (err) {
        console.error("API Error:", err);
      }

      setTests(enriched);
    } catch (err) {
      console.error(err);
      showToast('Failed to load tests', 'error');
    } finally {
      setLoading(false);
    }
  }, [company.id, showToast]);

  useEffect(() => {
    if (hasFetched) return;
    setHasFetched(true);



    loadTests();
  }, [hasFetched, loadTests]);

  const deleteTest = async (testId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this test and ALL its bug reports?')) return;

    try {
      await apiClient.deleteTest(testId);
      showToast('Test deleted successfully');
      loadTests();
    } catch (err) {
      console.error(err);
      showToast('Failed to delete test', 'error');
    }
  };

  const totalBugs = tests.reduce((acc, t) => acc + (t.bug_count || 0), 0);
  const criticalBugs = tests.reduce((acc, t) => acc + (t.critical_count || 0), 0);
  const totalTesters = tests.reduce((acc, t) => acc + (t.tester_count || 0), 0);

  return (
    <div className="dashboard-content">
      <header className="dashboard-top-row">
        <div className="dashboard-title-group">
          <h1>Dashboard</h1>
          <p className="dashboard-date">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <button className="new-project-btn" onClick={() => onViewChange('create-test')}>
          <Plus size={14} />
          <span>New test session</span>
        </button>
      </header>

      <div className="dashboard-stats-grid">
        {[
          { label: 'Active sessions', value: loading ? '...' : tests.length, desc: 'currently running' },
          { label: 'Total issues', value: loading ? '...' : totalBugs, desc: 'across all audits' },
          {
            label: 'Critical bugs',
            value: loading ? '...' : criticalBugs,
            desc: 'requiring immediate fix',
            isCritical: true
          },
          { label: 'Testers', value: loading ? '...' : totalTesters, desc: 'assigned to projects' },
        ].map((card, idx) => {
          let cardClass = "dashboard-stat-card";
          if (card.isCritical) {
            cardClass += card.value === 0 ? " card-success" : (card.value === '...' ? '' : " card-danger");
          }

          return (
            <div key={idx} className={cardClass}>
              <span className="stat-label">{card.label}</span>
              <div className="stat-value">{card.value}</div>
              <span className="stat-desc">{card.desc}</span>
            </div>
          );
        })}
      </div>

      <div className="dashboard-projects-list">
        <h2 className="dashboard-section-title">Your test sessions</h2>

        <div className="projects-table-container">
          {loading ? (
            <div className="projects-list">
              {[1, 2, 3].map(i => (
                <div key={i} className="project-list-row skeleton" style={{ height: '72px', border: 'none' }}></div>
              ))}
            </div>
          ) : tests.length === 0 ? (
            <div className="empty-projects">
              <div className="empty-projects-art" style={{ marginBottom: '24px' }}>


                <img src="/assets/good.png" alt="No sessions" width="200" />

              </div>
              <h3>No sessions yet</h3>
              <p>Ready to start quality assurance? Start your first test session to begin tracking bugs and issues.</p>
              <button className="empty-create-btn" onClick={() => onViewChange('create-test')}>
                <Plus size={18} />
                Start your first session
              </button>
            </div>
          ) : (
            <div className="projects-list">
              {tests.map(test => {
                const name = test.app_name || test.name || 'Untitled';
                const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

                return (
                  <div
                    key={test.id}
                    className="project-list-row"
                    onClick={() => {
                      onSelectTest(test);
                      onViewChange('test-detail');
                    }}
                  >
                    <div className="project-id-cell">
                      <div className="project-monogram">{initials}</div>
                      <div className="project-name-group">
                        <div className="project-row-name">{name}</div>
                        <div className="project-row-date">
                          {new Date(test.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                    </div>

                    <div className="project-stat-cell">
                      <div className="project-row-val">{test.bug_count || 0}</div>
                      <div className="project-row-lbl">Issues</div>
                    </div>

                    <div className="project-stat-cell">
                      <div className={`project-row-val ${test.critical_count > 0 ? 'text-danger' : 'text-success'}`}>
                        {test.critical_count || 0}
                      </div>
                      <div className="project-row-lbl">Critical</div>
                    </div>

                    <div className="project-status-cell">
                      <div className={`status-pill ${test.status || 'pending'}-pill`}>
                        {test.status ? test.status.charAt(0).toUpperCase() + test.status.slice(1) : 'Pending'}
                      </div>
                      <div className="view-report-link">View report &rarr;</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}